import { useEffect, useMemo, useState } from 'react'
import { useLocalization } from '../../Hooks/useLocalization'
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner'
import './TableList.scss'
import {sortResultNumber, sortResultString} from '../../Utils/Arrays'
import { FaIcon } from '../FaIcon/FaIcon'
import { useDispatch, useSelector } from 'react-redux'
import { type RootState } from '../../Store/Store'
import { Button } from '../Button/Button'
import { exportCsv, exportExcel } from './TableListExport'
import {
  setAnnouncements,
  setCertificateInstances,
  setCertificates,
  setCertificateTypes,
  setCompanies,
  setIsAnnouncementsRequested,
  setIsCertificateInstancesRequested,
  setIsCertificatesRequested,
  setIsCertificateTypesRequested,
  setIsCompaniesRequested,
  setIsLoggingsRequested,
  setIsUsersRequested,
  setLoggings,
  setUsers,
} from '../../Store/DataSlice'

export interface configuration {
  show: boolean
  label: string
  getValue?: (value: unknown, key: string) => string
  getSortValue?: (value: unknown, key: string) => string | number
  getElementValue?: (value: unknown, key: string) => JSX.Element
  getCsvExportValue?:
    | false
    | ((value: unknown, key: string) => string | undefined)
  getExcelExportValue?:
    | false
    | ((value: unknown, key: string) => string | Date | number | undefined)
}

interface TableListProps<T> {
  tableType: string
  items: T[]
  configurations: {
    [property in keyof T]: configuration
  }
  localizationSection: string
  getUniqueKey: (t: T) => string
  onRowClick?: (t: T) => void
  isLoading: boolean
}

const baseDisplayLimit = 50
declare type SortDirection = 'none' | 'ascending' | 'descending'

export function TableList<T>({
  tableType,
  items,
  configurations,
  localizationSection,
  getUniqueKey,
  onRowClick,
  isLoading,
}: TableListProps<T>) {
  const localization = useLocalization(localizationSection)
  const user = useSelector((state: RootState) => state.authentication.user)
  const isLoggedIn = user != null
  const componentsLocalization = useLocalization('components')
  const dispatch = useDispatch()
  const [sortProperty, setSortProperty] = useState('')
  const [sortDirection, setSortDirection] = useState<SortDirection>('none')
  const triggerSort = (property: string) => {
    if (property !== sortProperty || sortDirection === 'none') {
      setSortProperty(property)
      setSortDirection('ascending')
    } else if (sortDirection === 'ascending') setSortDirection('descending')
    else setSortDirection('none')
  }

  const [currentDisplayLimit, setCurrentDisplayLimit] =
    useState(baseDisplayLimit)

  const valueItems = useMemo(() => {
    const configs = configurations as Record<string, configuration>
    return items.map((item) => {
      const uniqueKey = getUniqueKey(item)
      const valueItem: Record<string, string> = {
        ...(item as unknown as Record<string, string>),
      }
      const sortValueItem: Record<string, string | number> = {
        ...(item as unknown as Record<string, string | number>),
      }
      for (const fieldName in configs) {
        const configuration = configs[fieldName]
        valueItem[fieldName] = configuration.getValue
          ? configuration.getValue(valueItem[fieldName], uniqueKey)
          : valueItem[fieldName]
        sortValueItem[fieldName] = configuration.getSortValue ? configuration.getSortValue(sortValueItem[fieldName], uniqueKey) : valueItem[fieldName]
      }
      return { item, uniqueKey, valueItem, sortValueItem }
    })
  }, [configurations, getUniqueKey, items])

  const sortedRows = useMemo(() => {
    const configuration = (configurations as Record<string, configuration>)[
      sortProperty
    ]
    if (sortDirection === 'none' || configuration == null) return valueItems
    else {
      return [...valueItems].sort((a, b) => {
        const aValue = a.sortValueItem[sortProperty] ?? ''
        const bValue = b.sortValueItem[sortProperty] ?? ''
        const sortResult = (typeof aValue === 'number' && typeof bValue === 'number') ? sortResultNumber(aValue, bValue) : sortResultString('' + aValue, '' + bValue)
        if (sortDirection === 'descending') return sortResult * -1
        return sortResult
      })
    }
  }, [configurations, sortProperty, sortDirection, valueItems])

  useEffect(() => {
    // reset display limit whenever we search to have as short a display list to render as possible

    const topOffset = document.getElementById('table-list')?.offsetTop ?? 0
    if (window.scrollY > topOffset) window.scrollTo({ top: topOffset })
    setTimeout(() => {
      setCurrentDisplayLimit(baseDisplayLimit)
    }, 1)
  }, [sortedRows, sortProperty, sortDirection])

  useEffect(() => {
    const pageDocument = document.documentElement
    if (pageDocument != null && sortedRows.length > currentDisplayLimit) {
      const scrollCheck = () => {
        if (
          pageDocument.scrollTop >
          pageDocument.scrollHeight -
            pageDocument.clientHeight -
            Math.max(window.innerHeight / 2, 250)
        ) {
          setCurrentDisplayLimit((p) => p + baseDisplayLimit)
        }
      }
      window.addEventListener('scroll', scrollCheck)
      return () => {
        window.removeEventListener('scroll', scrollCheck)
      }
    }
  }, [currentDisplayLimit, sortedRows])

  const reloadEverything = () => {
    dispatch(setCertificates([]))
    dispatch(setIsCertificatesRequested(false))
    dispatch(setCompanies([]))
    dispatch(setIsCompaniesRequested(false))
    dispatch(setCertificateInstances([]))
    dispatch(setIsCertificateInstancesRequested(false))
    dispatch(setUsers([]))
    dispatch(setIsUsersRequested(false))
    dispatch(setCertificateTypes([]))
    dispatch(setIsCertificateTypesRequested(false))
    dispatch(setLoggings([]))
    dispatch(setIsLoggingsRequested(false))
    dispatch(setIsLoggingsRequested(true))
    dispatch(setAnnouncements([]))
    dispatch(setIsAnnouncementsRequested(false))
  }

  return (
    <div className="table-list" id="table-list">
      {isLoading ? (
        <div className="load-in-progress">
          <LoadingSpinner color="dark" />
          {componentsLocalization.text(
            'data-being-loaded',
            'De data word momenteel geladen.'
          )}
        </div>
      ) : (
        <>
          <div className="table-buttons">
            <Button
              importance={'primary'}
              className="reload-everything"
              size="small"
              text={componentsLocalization.text(
                'reload-everything',
                'Herlaad data'
              )}
              onClick={reloadEverything}
            />
            {isLoggedIn && (
              <>
                <Button
                  importance={'primary'}
                  size="small"
                  text={componentsLocalization.text('export-csv', 'Export csv')}
                  onClick={() => {
                    exportCsv(tableType, items, configurations, getUniqueKey)
                  }}
                />
                <Button
                  importance={'primary'}
                  size="small"
                  text={componentsLocalization.text(
                    'export-excel',
                    'Export excel'
                  )}
                  onClick={() => {
                    exportExcel(tableType, items, configurations, getUniqueKey)
                  }}
                />
              </>
            )}
          </div>
          <div className="table-wrapper">
            <div className="table-header">
              {Object.keys(configurations)
                .filter(
                  (property) =>
                    (configurations as Record<string, configuration>)[property]
                      .show
                )
                .map((property) => (
                  <div
                    key={property}
                    className={`column-${property}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      triggerSort(property)
                    }}
                  >
                    {sortProperty === property && sortDirection !== 'none' ? (
                      <span className="sort-indicator">
                        {sortDirection === 'ascending' ? (
                          <FaIcon iconName="faSortUp" />
                        ) : (
                          <FaIcon iconName="faSortDown" />
                        )}
                      </span>
                    ) : (
                      <span className="sort-indicator sort-inactive">
                        <FaIcon iconName="faSort" />
                      </span>
                    )}
                    {localization.text(
                      property,
                      (configurations as Record<string, configuration>)[
                        property
                      ].label
                    )}
                  </div>
                ))}
            </div>
            {sortedRows.slice(0, currentDisplayLimit).map((row, index) => (
              <div
                className={`data-row ${index % 2 ? 'uneven' : 'even'} ${
                  onRowClick ? 'clickable' : ''
                }`}
                key={row.uniqueKey}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(row.item)
                      }
                    : undefined
                }
              >
                {Object.keys(configurations)
                  .filter(
                    (property) =>
                      (configurations as Record<string, configuration>)[
                        property
                      ].show
                  )
                  .map((property) => {
                    const configuration = (
                      configurations as Record<string, configuration>
                    )[property]

                    return (
                      <div key={property} className={`column-${property}`}>
                        <span className="data-label">
                          {localization.text(property, configuration.label)}
                        </span>
                        <span className="data">
                          {configuration.getElementValue
                            ? configuration.getElementValue(
                                (row.item as Record<string, unknown>)[property],
                                row.uniqueKey
                              )
                            : row.valueItem[property]}
                        </span>
                      </div>
                    )
                  })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
